// SegmentEditor.tsx - Fullscreen modal for editing segments

import React, { useState, useEffect } from 'react';
import { Plus, X, Trash2, Save, RefreshCw, CheckSquare, Square } from 'lucide-react';
import { Segment } from './types';
import { formatTime } from './helpers';
import { createComboFromSourceIds, getRandomColor, isCombo, renameGeneratedSourceSegmentsByTimeline } from './utils';

interface SegmentEditorProps {
  segments: Segment[];
  currentTime: number;
  onUpdateSegments: (segments: Segment[]) => void;
  onClose: () => void;
}

const SegmentEditor: React.FC<SegmentEditorProps> = ({
  segments,
  currentTime,
  onUpdateSegments,
  onClose
}) => {
  const [editingSegments, setEditingSegments] = useState<Segment[]>(segments);
  const [hasChanges, setHasChanges] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    setEditingSegments(segments);
  }, [segments]);

  const learnedCount = editingSegments.filter(s => s.isLearned).length;
  const hasLearnedTicks = learnedCount > 0;

  const handleAddSegment = () => {
    const usedColors = editingSegments.map(s => s.color);
    const segmentNumber = editingSegments.length + 1;
    
    const newSegment: Segment = {
      id: `segment-${Date.now()}`,
      name: `Segment ${segmentNumber}`,
      startTime: currentTime,
      endTime: currentTime + 5,
      color: getRandomColor(usedColors),
      transitionDuration: 0.5,
      isLearned: false,
      isDisabled: false
    };
    
    setEditingSegments(renameGeneratedSourceSegmentsByTimeline([...editingSegments, newSegment]));
    setHasChanges(true);
  };

  const handleDeleteSegment = (segmentId: string) => {
    setEditingSegments(renameGeneratedSourceSegmentsByTimeline(editingSegments.filter(s => s.id !== segmentId)));
    setHasChanges(true);
  };

  const handleUpdateSegmentName = (segmentId: string, newName: string) => {
    setEditingSegments(
      editingSegments.map(s => 
        s.id === segmentId
          ? { ...s, name: newName, ...(isCombo(s) ? { comboNameEdited: true } : { segmentNameEdited: true }) }
          : s
      )
    );
    setHasChanges(true);
    setEditingId(null);
  };

  const handleUpdateSegmentTime = (segmentId: string, field: 'startTime' | 'endTime', value: string) => {
    const timeValue = parseFloat(value);
    if (isNaN(timeValue)) return;
    
    setEditingSegments(renameGeneratedSourceSegmentsByTimeline(
      editingSegments.map(s => 
        s.id === segmentId ? { ...s, [field]: timeValue } : s
      )
    ));
    setHasChanges(true);
  };

  const handleToggleLearned = (segmentId: string) => {
    setEditingSegments(
      editingSegments.map(s => 
        s.id === segmentId ? { ...s, isLearned: !s.isLearned } : s
      )
    );
    setHasChanges(true);
  };

  const handleToggleAllLearned = () => {
    const shouldClear = hasLearnedTicks;
    setEditingSegments(
      editingSegments.map(s => ({ ...s, isLearned: !shouldClear }))
    );
    setHasChanges(true);
  };

  const handleMergeLearnedSegments = () => {
    const learned = editingSegments.filter(s => s.isLearned && !isCombo(s));
    
    if (learned.length < 2) {
      alert("Please mark at least two segments as learned before merging.");
      return;
    }
    
    learned.sort((a, b) => a.startTime - b.startTime);
    
    const mergedSourceIds = new Set(learned.map(segment => segment.id));
    const mergedSegment = createComboFromSourceIds(
      editingSegments,
      learned.map(segment => segment.id),
      0.5,
      `combo-${Date.now()}`,
      learned[0].id,
      learned[learned.length - 1].id
    );

    if (!mergedSegment) return;
    
    setEditingSegments([
      ...editingSegments.map(segment =>
        mergedSourceIds.has(segment.id)
          ? { ...segment, isLearned: false }
          : segment
      ),
      mergedSegment
    ]);
    setHasChanges(true);
  };

  const handleSave = () => {
    const sortedSegments = renameGeneratedSourceSegmentsByTimeline(
      [...editingSegments].sort((a, b) => a.startTime - b.startTime)
    );
    onUpdateSegments(sortedSegments);
    setHasChanges(false);
    onClose();
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
        return;
      }
    }
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        width: '90%',
        maxWidth: '50rem',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
              Edit Segments
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {editingSegments.length} segment{editingSegments.length !== 1 ? 's' : ''} • Current time: {formatTime(currentTime)}
            </p>
          </div>
          <button
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Clear all / Tick all button */}
        {editingSegments.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={handleToggleAllLearned}
              style={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                borderRadius: '0.5rem',
                border: '1px solid #d1d5db',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
              title={hasLearnedTicks ? "Clear all learned ticks" : "Tick all segments as learned"}
            >
              {hasLearnedTicks ? <Square size={16} /> : <CheckSquare size={16} />}
              {hasLearnedTicks ? "Clear all" : "Tick all"} ({learnedCount}/{editingSegments.length})
            </button>
          </div>
        )}

        {/* Segments List */}
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem' }}>
          {editingSegments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <p style={{ marginBottom: '1rem' }}>No segments yet</p>
              <button
                onClick={handleAddSegment}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9375rem',
                  fontWeight: '500'
                }}
              >
                <Plus size={16} />
                Add First Segment
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {editingSegments.map((segment, index) => (
                <div
                  key={segment.id}
                  style={{
                    border: '2px solid #e5e7eb',
                    borderLeft: `4px solid ${segment.color}`,
                    borderRadius: '0.5rem',
                    padding: '1rem',
                    backgroundColor: segment.isLearned ? '#f0fdf4' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={segment.isLearned || false}
                        onChange={() => handleToggleLearned(segment.id)}
                        style={{ width: '1rem', height: '1rem' }}
                        title="Mark as learned"
                      />
                      
                      {editingId === segment.id ? (
                        <input
                          type="text"
                          defaultValue={segment.name}
                          onBlur={(e) => handleUpdateSegmentName(segment.id, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateSegmentName(segment.id, e.currentTarget.value);
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            border: '1px solid #3b82f6',
                            borderRadius: '0.375rem',
                            fontSize: '1rem',
                            fontWeight: '600'
                          }}
                          autoFocus
                        />
                      ) : (
                        <h3
                          onClick={() => setEditingId(segment.id)}
                          style={{
                            fontSize: '1rem',
                            fontWeight: segment.isLearned ? '700' : '600',
                            cursor: 'pointer',
                            opacity: 1
                          }}
                        >
                          {segment.name}
                        </h3>
                      )}
                    </div>
                    
                    <button
                      onClick={() => handleDeleteSegment(segment.id)}
                      style={{
                        padding: '0.375rem',
                        backgroundColor: '#fef2f2',
                        color: '#dc2626',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer'
                      }}
                      title="Delete segment"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.25rem' }}>
                        Start Time (s)
                      </label>
                      <input
                        type="number"
                        value={segment.startTime.toFixed(2)}
                        onChange={(e) => handleUpdateSegmentTime(segment.id, 'startTime', e.target.value)}
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem'
                        }}
                      />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.25rem' }}>
                        End Time (s)
                      </label>
                      <input
                        type="number"
                        value={segment.endTime.toFixed(2)}
                        onChange={(e) => handleUpdateSegmentTime(segment.id, 'endTime', e.target.value)}
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                    Duration: {formatTime(segment.endTime - segment.startTime)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {editingSegments.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleAddSegment}
                style={{
                  flex: 1,
                  padding: '0.625rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                <Plus size={16} />
                Add Segment
              </button>
              
              {learnedCount >= 2 && (
                <button
                  onClick={handleMergeLearnedSegments}
                  style={{
                    flex: 1,
                    padding: '0.625rem',
                    backgroundColor: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    fontSize: '0.875rem',
                    fontWeight: '500'
                  }}
                >
                  <RefreshCw size={16} />
                  Merge Learned ({learnedCount})
                </button>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontSize: '0.9375rem',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges}
              style={{
                flex: 1,
                padding: '0.75rem',
                backgroundColor: hasChanges ? '#16a34a' : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: hasChanges ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                fontSize: '0.9375rem',
                fontWeight: '600'
              }}
            >
              <Save size={16} />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentEditor;
